import type {
    NdcPathwaySwitchConfig,
    NdcSwitchConfig,
    NdcSwitchTargetV2,
    SwitchRequestSource,
} from '../../../types/drug-config';
import type { MedicationProduct, OnHoldConfig } from '../../shared';

const switchFromVial: Omit<NdcSwitchConfig, 'targets'> = {
    heading: '',
    description: '',
    link: {
        heading: '',
        text: 'See other options',
        position: 'details',
    },
};

const medicareBridgeSwitchContent: NdcPathwaySwitchConfig['content'] = {
    details: {
        whatsNext: [
            'Click "Submit request" to send a fax to your healthcare provider requesting a prescription for the Zepbound® Multi-Dose KwikPen®. The request comes from you — we just handle the sending and receiving so you don\'t have to.',
            'Once you request the new prescription(s), your current prescription will be placed on hold.',
        ],
        whatYouGet: {
            bullets: [
                {
                    key: 'same-med',
                    content: 'Same medication, different device.',
                    icon: 'check',
                    iconColor: 'success',
                },
                {
                    key: 'medicare-bridge',
                    content:
                        'May be covered by the Medicare GLP\u20111 Bridge program.',
                    icon: 'check',
                    iconColor: 'success',
                },
                {
                    key: 'prior-auth',
                    content:
                        '<b>Prior authorization (PA)</b><br />Medicare GLP\u20111 Bridge requires insurance approval with support from your provider, which may take time.',
                    icon: 'warning',
                    iconColor: 'warning',
                },
            ],
        },
        whySwitch: [
            "The Zepbound® Single-Dose Vial is only available for self-pay. To continue with the Medicare GLP\u20111 Bridge program, you'll need a prescription for the Zepbound® Multi-Dose KwikPen®.",
        ],
    },
    pageDescription: [
        "To use Medicare GLP\u20111 Bridge, you'll need to switch from the Zepbound® Single-Dose Vial to the <b>Zepbound® Multi-Dose KwikPen®</b>.",
    ],
    pageTitle: 'Request a new prescription',
};

const medicareKPSwitchTarget: Omit<NdcSwitchTargetV2, 'ndc'> = {
    description: 'Zepbound® Multi-Dose KwikPen®',
    iconUrl: '/static/img/kwikpen-small.png',
    learnMoreModal: {
        description: [
            'Zepbound® KwikPen® is a multi-dose pen that holds four doses of Zepbound® medicine, one for each week of the month. Each injection uses a new pen needle, and you can purchase pen needles at checkout.',
        ],
        title: 'Zepbound® Multi-Dose KwikPen® for Single Patient Use',
    },
};

const vialSwitchSource: SwitchRequestSource = {
    description: 'Zepbound® Single-Dose Vial',
    iconUrl: '/static/img/vial-small.png',
    keepCurrent: {
        returnPathway: 'self-pay',
        modal: {
            description: [
                "As a reminder, your current prescription (Zepbound® Single-Dose Vial) is only available for self-pay. To continue with the Medicare GLP\u20111 Bridge program, you'll need a prescription for the Zepbound® Multi-Dose KwikPen®.",
            ],
            title: 'Are you sure?',
        },
    },
};

const pathway: MedicationProduct['pathway'] = {
    initialPathway: 'self-pay',
    initialScreen: 'pharmacy-select',
};

const onHoldConfig: OnHoldConfig = {
    allowHold: true,
    pathways: ['self-pay'],
    screens: ['pharmacy-select', 'pharmacy-preview'],
    buttonText: 'See other options',
    modal: {
        title: 'Other options',
        body: [
            'If you were expecting a different medicine or form of this medicine, there may be other options available. Consult with your provider to discuss these options.',
            "If you choose 'Confirm and continue' we will put your prescription on hold until we receive a response from your provider or you send it to a pharmacy using the LillyDirect medicine cabinet.",
        ],
        buttonLabels: {
            close: 'Close',
            confirm: 'Confirm and continue',
        },
    },
    holdScreen: {
        alert: {
            color: 'warning',
            body: [
                {
                    key: 'on-hold',
                    text: '<body1>Your current Zepbound® Single-Dose Vial prescription is on hold</body1>',
                },
            ],
        },
        body: {
            default: [
                {
                    key: 'default-1',
                    text: '<body1>You recently placed your Zepbound prescription(s) on hold so you could speak with your provider. All Zepbound prescriptions of this form in your treatment plan have been temporarily locked while the hold is in place.</body1>',
                },
                {
                    key: 'default-2',
                    text: "<body1>If you're ready to move forward, you can unlock the prescription(s) by confirming below. Once unlocked, the hold will be removed and the prescription(s) will be processed as-is.</body1>",
                },
            ],
            withChangeReq: [
                {
                    key: 'with-change-1',
                    text: "<body1>You've sent a fax to your healthcare provider to request a change from the Zepbound® Single-Dose Vial to <b>Zepbound® Multi-Dose KwikPen®</b> so you can proceed with the Medicare GLP\u20111 Bridge program.</body1>",
                },
                {
                    key: 'with-change-2',
                    text: "<body1>This typically takes 1–3 business days. If you haven't heard anything by then, you can reach out to your provider's office.</body1>",
                },
            ],
        },
        title: {
            withChangeReq: 'Request submitted for:',
        },
        buttonLabels: {
            home: 'Return to home',
            unhold: 'Continue with current prescription',
        },
        confirmModal: {
            title: 'Are you sure?',
            body: [
                'You recently requested to change your Zepbound® prescription(s) to the Zepbound® Multi-Dose KwikPen®. Your request has been sent, and all Zepbound® prescriptions of this form in your treatment plan are on hold while we wait for a response.',
                "If you've changed your mind and want to proceed with the Zepbound® Single-Dose Vial, you can remove the hold by confirming below and the prescription will be processed as-is. If your provider sends a new prescription anyway, we'll notify you.",
                "As a reminder, your current prescription (Zepbound® Single-Dose Vial) is only available for self-pay. To continue with the Medicare GLP\u20111 Bridge program, you'll need a prescription for the Zepbound® Multi-Dose KwikPen®.",
            ],
            checkboxLabel:
                "I confirm that I've decided to keep the relevant prescription(s). I understand that I no longer expect a prescription change from my provider, and it is my responsibility to notify them that I am proceeding with the original prescription(s).",
            buttonLabels: {
                confirm: 'Confirm and continue',
            },
        },
        toast: {
            default: 'A request has been sent to your doctor.',
        },
    },
};

// Vial → KwikPen switching is not yet live. Each vial product retains a switchConfig
// with empty targets so the "See other options" link remains visible in the UI.
export const zepboundVials: MedicationProduct[] = [
    {
        description: 'Zepbound® 2.5 mg/0.5 mL Single-Dose Vial',
        dosageForm: 'SOLN',
        formLabel: 'Vial',
        packageSizeUnit: 'ML',
        strengthUnit: 'mg',
        strengthValue: 2.5,
        ndcs: [
            { ndc: '00002015201', packageQuantity: 1 },
            { ndc: '00002015204', packageQuantity: 4 },
            { ndc: '00002015261', packageQuantity: 4 },
        ],
        pricing: { selfPay: 299, regularPrice: 349 },
        // Incentive === Cash here is intentional: this dose has no real Journey
        // Program discount. SelfPayIncentivePricingTerms.tsx infers "is this a
        // Journey NDC" from Incentive being strictly lower than Cash (see MYPT-943)
        // — if you change either value, check whether that still holds correctly.
        headlinePrices: [
            { type: 'Cash', value: 299 },
            {
                type: 'Incentive',
                value: 299,
                qualifier: { text: '/month' },
            },
        ],
        switchConfig: { ...switchFromVial, targets: [] },
        switchConfigV2: {
            government: [
                {
                    content: medicareBridgeSwitchContent,
                    subPathways: ['medicare-bridge'],
                    source: vialSwitchSource,
                    targets: [
                        {
                            ...medicareKPSwitchTarget,
                            ndc: '00002356601',
                        },
                    ],
                },
            ],
        },
        onHoldConfig,
        pathway,
    },
    {
        description: 'Zepbound® 5 mg/0.5 mL Single-Dose Vial',
        dosageForm: 'SOLN',
        formLabel: 'Vial',
        packageSizeUnit: 'ML',
        strengthUnit: 'mg',
        strengthValue: 5,
        ndcs: [
            { ndc: '00002024301', packageQuantity: 1 },
            { ndc: '00002024304', packageQuantity: 4 },
        ],
        pricing: { selfPay: 399, regularPrice: 499 },
        // Incentive === Cash here is intentional — see the 2.5mg comment above.
        headlinePrices: [
            { type: 'Cash', value: 399 },
            {
                type: 'Incentive',
                value: 399,
                qualifier: { text: '/month' },
            },
        ],
        switchConfig: { ...switchFromVial, targets: [] },
        switchConfigV2: {
            government: [
                {
                    content: medicareBridgeSwitchContent,
                    subPathways: ['medicare-bridge'],
                    source: vialSwitchSource,
                    targets: [
                        {
                            ...medicareKPSwitchTarget,
                            ndc: '00002355501',
                        },
                    ],
                },
            ],
        },
        onHoldConfig,
        pathway,
    },
    {
        description: 'Zepbound® 7.5 mg/0.5 mL Single-Dose Vial',
        dosageForm: 'SOLN',
        formLabel: 'Vial',
        packageSizeUnit: 'ML',
        strengthUnit: 'mg',
        strengthValue: 7.5,
        ndcs: [
            { ndc: '00002121401', packageQuantity: 1 },
            { ndc: '00002121404', packageQuantity: 4 },
        ],
        pricing: { selfPay: 449, regularPrice: 599 },
        headlinePrices: [
            { type: 'Cash', value: 499 },
            {
                type: 'Incentive',
                value: 449,
                qualifier: { text: '/month' },
            },
        ],
        switchConfig: { ...switchFromVial, targets: [] },
        switchConfigV2: {
            government: [
                {
                    content: medicareBridgeSwitchContent,
                    subPathways: ['medicare-bridge'],
                    source: vialSwitchSource,
                    targets: [
                        {
                            ...medicareKPSwitchTarget,
                            ndc: '00002354401',
                        },
                    ],
                },
            ],
        },
        onHoldConfig,
        pathway,
    },
    {
        description: 'Zepbound® 10 mg/0.5 mL Single-Dose Vial',
        dosageForm: 'SOLN',
        formLabel: 'Vial',
        packageSizeUnit: 'ML',
        strengthUnit: 'mg',
        strengthValue: 10,
        ndcs: [
            { ndc: '00002134001', packageQuantity: 1 },
            { ndc: '00002134004', packageQuantity: 4 },
        ],
        pricing: { selfPay: 449, regularPrice: 699 },
        headlinePrices: [
            { type: 'Cash', value: 699 },
            {
                type: 'Incentive',
                value: 449,
                qualifier: { text: '/month' },
            },
        ],
        switchConfig: { ...switchFromVial, targets: [] },
        switchConfigV2: {
            government: [
                {
                    content: medicareBridgeSwitchContent,
                    subPathways: ['medicare-bridge'],
                    source: vialSwitchSource,
                    targets: [
                        {
                            ...medicareKPSwitchTarget,
                            ndc: '00002353301',
                        },
                    ],
                },
            ],
        },
        onHoldConfig,
        pathway,
    },
    {
        description: 'Zepbound® 12.5 mg/0.5 mL Single-Dose Vial',
        dosageForm: 'SOLN',
        formLabel: 'Vial',
        packageSizeUnit: 'ML',
        strengthUnit: 'mg',
        strengthValue: 12.5,
        ndcs: [
            { ndc: '00002142301', packageQuantity: 1 },
            { ndc: '00002142304', packageQuantity: 4 },
        ],
        pricing: { selfPay: 449, regularPrice: 849 },
        headlinePrices: [
            { type: 'Cash', value: 699 },
            {
                type: 'Incentive',
                value: 449,
                qualifier: { text: '/month' },
            },
        ],
        switchConfig: { ...switchFromVial, targets: [] },
        switchConfigV2: {
            government: [
                {
                    content: medicareBridgeSwitchContent,
                    subPathways: ['medicare-bridge'],
                    source: vialSwitchSource,
                    targets: [
                        {
                            ...medicareKPSwitchTarget,
                            ndc: '00002352201',
                        },
                    ],
                },
            ],
        },
        onHoldConfig,
        pathway,
    },
    {
        description: 'Zepbound® 15 mg/0.5 mL Single-Dose Vial',
        dosageForm: 'SOLN',
        formLabel: 'Vial',
        packageSizeUnit: 'ML',
        strengthUnit: 'mg',
        strengthValue: 15,
        ndcs: [
            { ndc: '00002200201', packageQuantity: 1 },
            { ndc: '00002200204', packageQuantity: 4 },
        ],
        pricing: { selfPay: 449, regularPrice: 1049 },
        headlinePrices: [
            { type: 'Cash', value: 699 },
            {
                type: 'Incentive',
                value: 449,
                qualifier: { text: '/month' },
            },
        ],
        switchConfig: { ...switchFromVial, targets: [] },
        switchConfigV2: {
            government: [
                {
                    content: medicareBridgeSwitchContent,
                    subPathways: ['medicare-bridge'],
                    source: vialSwitchSource,
                    targets: [
                        {
                            ...medicareKPSwitchTarget,
                            ndc: '00002351101',
                        },
                    ],
                },
            ],
        },
        onHoldConfig,
        pathway,
    },
];
